import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowRight, Sparkles, Play, Pause } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { useTypewriter } from '../../hooks/useTypewriter';

interface UpsellSlide {
  title: string;
  subtitle: string;
  videoUrl?: string;
  imageUrl?: string;
}

interface UpsellModalProps {
  currentAmount: number;
  onClose: () => void;
  onUpgrade: (amount: number) => void;
}

const fallbackSlide: UpsellSlide = {
  title: '阿占隨意 DIY 手機殼',
  subtitle: '捐款滿 $500 即可免費獲得',
};

const TYPEWRITER_TEXT = '善良值得被記住，這個機殼就是最好的憑證 💫';
const TYPEWRITER_SPEED = 200; // ms per char
const TYPEWRITER_PAUSE = 2000; // ms pause before restart

function UpsellModalContent({ currentAmount, onClose, onUpgrade }: UpsellModalProps) {
  const [slide, setSlide] = useState<UpsellSlide>(fallbackSlide);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { display: typedText, showCursor } = useTypewriter(
    TYPEWRITER_TEXT,
    TYPEWRITER_SPEED,
    TYPEWRITER_PAUSE
  );

  // Modal 打開時鎖定 body scroll，防止 viewport 寬度變化導致抖動
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // 只取 API 第一張 slide 的資料
  useEffect(() => {
    apiFetch<{ title?: string; subtitle?: string; videoUrl?: string; imageUrl?: string }[]>('/api/public/upsell-slides')
      .then((res) => {
        if (res.success && res.data && res.data.length > 0) {
          const first = res.data[0];
          setSlide({
            title: first.title || fallbackSlide.title,
            subtitle: first.subtitle || fallbackSlide.subtitle,
            videoUrl: first.videoUrl,
            imageUrl: first.imageUrl,
          });
        }
      })
      .catch(() => { /* 維持 fallback */ });
  }, []);

  // 三重保險循環播放：loop 屬性 + ended 事件 + timeupdate 監聽
  const handleEnded = useCallback(() => {
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      if (!isPausedRef.current) {
        v.play().catch(() => {});
      }
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v || v.duration <= 0 || isPausedRef.current) return;
    // 若距離結尾 0.3 秒內且未標記已觸發，強制重置
    if (v.currentTime >= v.duration - 0.3) {
      v.currentTime = 0;
      v.play().catch(() => {});
    }
  }, []);

  // 播放 / 暫停切換
  const togglePause = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) {
      v.play().catch(() => {});
      isPausedRef.current = false;
      setIsPaused(false);
    } else {
      v.pause();
      isPausedRef.current = true;
      setIsPaused(true);
    }
  }, []);

  // 同步 video 實際狀態到 UI
  const handlePause = useCallback(() => {
    isPausedRef.current = true;
    setIsPaused(true);
  }, []);

  const handlePlay = useCallback(() => {
    isPausedRef.current = false;
    setIsPaused(false);
  }, []);

  const upgradeAmount = 500;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', WebkitTransform: 'translateZ(0)', zIndex: 10000 }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl overflow-hidden relative"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          WebkitTransform: 'translate3d(-50%, -50%, 0)',
          backfaceVisibility: 'hidden',
          willChange: 'transform',
          width: 'min(512px, calc(100vw - 2rem))',
          maxHeight: 'min(98vh, 980px)',
          display: 'flex',
          flexDirection: 'column',
          isolation: 'isolate',
          contain: 'layout style paint',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-sm hover:bg-white transition-colors"
        >
          <X className="w-4 h-4 text-gray-600" />
        </button>

        {/* ====== Video / Image 區塊 ====== */}
        <div
          className="relative w-full overflow-hidden bg-black flex-shrink-0 upsell-media aspect-square max-h-[min(360px,45vh)] md:max-h-[400px] md:w-[400px] md:mx-auto md:rounded-b-3xl"
          style={{
            WebkitTransform: 'translateZ(0)',
            minHeight: 200,
          }}
        >
          {/* Video — 始終渲染，避免 DOM 切換閃動 */}
          <video
            ref={videoRef}
            src={slide.videoUrl || ''}
            muted
            playsInline
            preload="auto"
            autoPlay
            loop
            onEnded={handleEnded}
            onTimeUpdate={handleTimeUpdate}
            onLoadedData={() => setIsLoaded(true)}
            onPause={handlePause}
            onPlay={handlePlay}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: isLoaded && slide.videoUrl ? 1 : 0, WebkitTransform: 'translateZ(0)' }}
          />

          {/* Image fallback — video 未載入時顯示 */}
          {!isLoaded && slide.imageUrl && (
            <img
              src={slide.imageUrl}
              alt={slide.title}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
          )}

          {/* 純色 fallback — 無 video 無 image */}
          {(!slide.videoUrl && !slide.imageUrl) && (
            <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-orange-600" />
          )}

          {/* Pause / Play 按鈕 */}
          {slide.videoUrl && isLoaded && (
            <button
              onClick={(e) => { e.stopPropagation(); togglePause(); }}
              className="absolute top-4 left-4 z-20 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
              title={isPaused ? '播放' : '暫停'}
            >
              {isPaused ? <Play className="w-5 h-5 ml-0.5" /> : <Pause className="w-5 h-5" />}
            </button>
          )}

          {/* Overlay gradient for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* Text overlay — 靠近底部，星星與文字同行 */}
          <div className="absolute inset-x-0 bottom-0 flex items-end justify-center p-4 pb-5">
            <div className="text-white w-full">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Sparkles className="w-5 h-5 text-yellow-300 flex-shrink-0" />
                <h3 className="text-lg sm:text-xl font-bold drop-shadow-lg" style={{ textWrap: 'balance' }}>
                  {slide.title}
                </h3>
              </div>
              <p className="text-white/90 text-xs sm:text-sm drop-shadow-md text-center" style={{ textWrap: 'balance' }}>
                {slide.subtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
              <span className="text-lg">🍚</span>
            </div>
            <div>
              <h4 className="font-bold text-gray-900">多 $450，多 9 個家庭吃飽飯</h4>
              <p className="text-sm text-gray-500">還有一份專屬禮物等你</p>
            </div>
          </div>

          <div className="text-sm text-gray-700 leading-relaxed space-y-2">
            <p className="text-gray-600">$500 會變成這些具體的改變：</p>
            <div className="flex items-start gap-2">
              <span className="text-base mt-0.5 flex-shrink-0">🍚</span>
              <span><strong className="text-gray-900">10 個基層家庭</strong> 吃到熱飯</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-base mt-0.5 flex-shrink-0">👦</span>
              <span><strong className="text-gray-900">SEN 青年</strong> 獲得 5 小時職場訓練</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-base mt-0.5 flex-shrink-0">📱</span>
              <span>一款<strong className="text-brand-600"> 專屬你的【阿占隨意】DIY 手機殼</strong>（15款任選，原價 $199）</span>
            </div>
            <p className="text-xs text-brand-500 pt-1 font-medium min-h-[1.25rem]">
              {typedText}
              <span className={`inline-block w-[2px] h-3 ml-[1px] align-middle bg-brand-400 ${showCursor ? 'opacity-100' : 'opacity-0'}`} />
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              onClick={() => onUpgrade(upgradeAmount)}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-3.5 px-4 rounded-2xl transition-all shadow-lg hover:shadow-xl"
            >
              <span>選 $500</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold py-3.5 px-4 rounded-2xl transition-colors"
            >
              暫時 HK${currentAmount}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const UpsellModal = memo(function UpsellModal({ currentAmount, onClose, onUpgrade }: UpsellModalProps) {
  return createPortal(
    <UpsellModalContent
      currentAmount={currentAmount}
      onClose={onClose}
      onUpgrade={onUpgrade}
    />,
    document.body
  );
});

export default UpsellModal;
